/**
 * AdminWcDisputesTab — Unified WC disputes resolver (iter 94).
 *
 * Lists disputed matches across Authentic Tournament, Immersion League, and
 * Dream Team. Shows both submitted scores + screenshots side-by-side. Admin
 * can:
 *   • Run AI verification on demand (Claude vision) — auto-resolves at
 *     confidence ≥ 0.8.
 *   • Manually override the final score.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, AlertTriangle, RefreshCcw, Bot, Check, X,
  Trophy, Image as ImageIcon, Filter,
} from "lucide-react";

const MODE_META = {
  "tournament":  { label: "Tournament", color: "#FFD700" },
  "immersion":   { label: "Immersion",  color: "#3B82F6" },
  "dream-team":  { label: "Dream Team", color: "#10B981" },
};

export default function AdminWcDisputesTab() {
  const [mode, setMode] = useState("");
  const [data, setData] = useState({ total: 0, rows: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = mode ? `/admin/wc/disputes?mode=${mode}&limit=100` : "/admin/wc/disputes?limit=100";
      const { data: d } = await api.get(url);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [mode]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="admin-wc-disputes-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-mono text-[13px] uppercase tracking-[0.25em] inline-flex items-center gap-2 text-[#09090B] dark:text-[#F4F4F5]">
          <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
          WC Disputes · {data.total}
        </h2>
        <div className="flex items-center gap-1.5">
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            data-testid="admin-disputes-mode-filter"
            className="rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#0F0F12] px-2 py-1.5 text-[11px] font-mono"
          >
            <option value="">All modes</option>
            {Object.entries(MODE_META).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={load}
            data-testid="admin-disputes-refresh"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#FAFAFC] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
          >
            <RefreshCcw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8 text-[#EF4444]" data-testid="admin-disputes-loading" />
      ) : data.rows.length === 0 ? (
        <div className="text-center py-12 text-[#71717A] text-sm" data-testid="admin-disputes-empty">
          <Trophy className="w-8 h-8 mx-auto mb-2 text-[#10B981]" />
          No open disputes. Everyone's playing nice. 🎉
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-disputes-list">
          {data.rows.map((r) => (
            <DisputeCard key={`${r.mode}-${r.match_id}`} row={r} onResolved={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function DisputeCard({ row, onResolved }) {
  const [aiBusy, setAiBusy] = useState(false);
  const [verdict, setVerdict] = useState(row.ai_verdict || null);
  const meta = MODE_META[row.mode] || { label: row.mode, color: "#71717A" };

  const runAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/admin/wc/disputes/${row.mode}/${row.match_id}/ai-check`);
      setVerdict(data.verdict);
      if (data.auto_resolved) {
        toast.success(`AI auto-resolved (confidence ${data.verdict.confidence?.toFixed(2)})`);
        onResolved && onResolved();
      } else {
        toast.message(`AI verdict in. Confidence ${(data.verdict?.confidence ?? 0).toFixed(2)} — review and resolve.`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#EF4444]/30 bg-white dark:bg-[#1A1A1E] p-4 space-y-3" data-testid={`admin-dispute-card-${row.mode}-${row.match_id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest font-bold"
            style={{ background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55` }}
          >
            {meta.label}
          </span>
          <span className="font-mono text-[10px] text-[#71717A]">{row.match_id?.slice(0, 8)}…</span>
          {row.round && <span className="text-[10px] font-mono text-[#71717A]">· {String(row.round).toUpperCase()}</span>}
        </div>
        <div className="text-[10px] font-mono text-[#71717A]">
          Disputed {row.disputed_at ? new Date(row.disputed_at).toLocaleString() : "—"}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <SidePanel side="HOME" user={row.home} submission={row.home_submission} accent="#60A5FA" />
        <SidePanel side="AWAY" user={row.away} submission={row.away_submission} accent="#F97316" />
      </div>

      {verdict && <AiVerdictPanel verdict={verdict} />}

      <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
        <button
          type="button"
          onClick={runAi}
          disabled={aiBusy}
          data-testid={`admin-dispute-ai-${row.mode}-${row.match_id}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#A855F7] text-white text-[10px] font-mono uppercase tracking-widest font-bold px-3 py-1.5 hover:bg-[#9333EA] disabled:opacity-50"
        >
          {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bot className="w-3 h-3" />}
          {verdict ? "Re-run AI check" : "Run AI check"}
        </button>
        <ResolveControls row={row} verdict={verdict} onResolved={onResolved} />
      </div>
    </div>
  );
}

function SidePanel({ side, user, submission, accent }) {
  return (
    <div className="rounded-xl border border-[#E4E4E7] dark:border-[#2A2A30] bg-[#FAFAFC] dark:bg-[#0F0F12] p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[9px] uppercase tracking-widest" style={{ color: accent }}>
          {side}
        </div>
        <div className="text-[11px] font-bold">@{user?.username || "?"}</div>
      </div>
      <div className="text-center">
        {submission && submission.score_home != null ? (
          <div className="font-heading text-2xl font-extrabold tabular-nums" style={{ color: accent }}>
            {submission.score_home} – {submission.score_away}
          </div>
        ) : (
          <div className="text-[10px] font-mono text-[#71717A]">No submission yet</div>
        )}
      </div>
      {submission?.screenshot_url ? (
        <a
          href={submission.screenshot_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-md overflow-hidden border border-[#E4E4E7] dark:border-[#2A2A30] hover:border-[#A855F7] transition-colors"
        >
          <img
            src={submission.screenshot_url}
            alt={`${side} screenshot`}
            className="w-full h-32 object-cover"
            loading="lazy"
          />
        </a>
      ) : (
        <div className="rounded-md border border-dashed border-[#E4E4E7] dark:border-[#2A2A30] py-3 text-center text-[10px] font-mono text-[#71717A] flex items-center justify-center gap-1">
          <ImageIcon className="w-3 h-3" /> No screenshot
        </div>
      )}
      {submission?.submitted_at && (
        <div className="text-[9px] font-mono text-[#71717A] text-center">{new Date(submission.submitted_at).toLocaleString()}</div>
      )}
    </div>
  );
}

function AiVerdictPanel({ verdict }) {
  const conf = verdict.confidence ?? 0;
  const ok = conf >= 0.8;
  return (
    <div
      className="rounded-md border px-3 py-2 text-[11px] font-mono"
      style={{
        borderColor: ok ? "#10B98155" : "#FBBF2455",
        background: ok ? "#10B98110" : "#FBBF2410",
      }}
      data-testid="admin-dispute-ai-verdict"
    >
      <div className="flex items-center justify-between flex-wrap gap-1">
        <div className="inline-flex items-center gap-1.5 font-bold" style={{ color: ok ? "#10B981" : "#FBBF24" }}>
          <Bot className="w-3 h-3" /> AI {verdict.home_score != null ? `${verdict.home_score} – ${verdict.away_score}` : "no verdict"}
        </div>
        <div className="text-[10px]">
          Conf: <span className="font-bold" style={{ color: ok ? "#10B981" : "#FBBF24" }}>{conf.toFixed(2)}</span>
          {verdict.model && <span className="text-[#71717A]"> · {verdict.model}</span>}
        </div>
      </div>
      {verdict.reasoning && (
        <div className="mt-1 text-[10px] text-[#71717A] leading-snug">{verdict.reasoning}</div>
      )}
      {verdict.ran_at && (
        <div className="mt-0.5 text-[9px] text-[#71717A]">Ran {new Date(verdict.ran_at).toLocaleString()}</div>
      )}
    </div>
  );
}

function ResolveControls({ row, verdict, onResolved }) {
  const initHome = verdict?.home_score ?? row.home_submission?.score_home ?? 0;
  const initAway = verdict?.away_score ?? row.away_submission?.score_away ?? 0;
  const [home, setHome] = useState(initHome);
  const [away, setAway] = useState(initAway);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (verdict?.home_score != null) {
      setHome(verdict.home_score);
      setAway(verdict.away_score);
    }
  }, [verdict]);

  const resolve = async (preset) => {
    let hs = home, as = away;
    if (preset === "home-win") { hs = Math.max(Number(home), Number(away) + 1); as = Number(away); }
    if (preset === "away-win") { as = Math.max(Number(away), Number(home) + 1); hs = Number(home); }
    if (preset === "draw")     { hs = Number(home); as = Number(home); }
    setBusy(true);
    try {
      await api.post(`/admin/wc/disputes/${row.mode}/${row.match_id}/resolve`, {
        home_score: Number(hs), away_score: Number(as),
        note: note.trim() || null,
      });
      toast.success("Dispute resolved.");
      onResolved && onResolved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center flex-wrap gap-1.5">
      <input
        type="number" min="0" max="30" value={home}
        onChange={(e) => setHome(e.target.value)}
        data-testid={`admin-dispute-home-input-${row.mode}-${row.match_id}`}
        className="w-12 text-center bg-white dark:bg-[#0F0F12] border border-[#E4E4E7] dark:border-[#2A2A30] rounded-md text-[12px] font-bold py-1"
      />
      <span className="text-[#71717A] text-[11px]">–</span>
      <input
        type="number" min="0" max="30" value={away}
        onChange={(e) => setAway(e.target.value)}
        data-testid={`admin-dispute-away-input-${row.mode}-${row.match_id}`}
        className="w-12 text-center bg-white dark:bg-[#0F0F12] border border-[#E4E4E7] dark:border-[#2A2A30] rounded-md text-[12px] font-bold py-1"
      />
      <input
        type="text" value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="note (opt)"
        data-testid={`admin-dispute-note-${row.mode}-${row.match_id}`}
        className="bg-white dark:bg-[#0F0F12] border border-[#E4E4E7] dark:border-[#2A2A30] rounded-md text-[10px] py-1 px-2 w-28 font-mono"
        maxLength={200}
      />
      <button
        type="button"
        onClick={() => resolve()}
        disabled={busy}
        data-testid={`admin-dispute-resolve-${row.mode}-${row.match_id}`}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#10B981] text-white text-[10px] font-mono uppercase tracking-widest font-bold px-3 py-1.5 hover:bg-[#059669] disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        Resolve
      </button>
    </div>
  );
}
